import React, { useState, useEffect } from 'react'
import { Button, Col, Divider, Drawer, Row, Typography, message, Table } from 'antd'
import GenericSelect from '../../../../components/GenericSelect'
import InventoryList from '../InventoryList'
import inventorySrc from '../../inventorySrc'
import Utils from '../../../../utils/Utils'
import UISpinner from '../../../../components/UISpinner'

const { Title } = Typography

function CountsDrawer(props) {
  const [selectedShop, setSelectedShop] = useState(null)
  const [isSummary, setIsSummary] = useState(false)
  const [dataSource, setDataSource] = useState([])
  const [productsList, setProductsList] = useState([])
  const [productsInfo, setProductsInfo] = useState([])
  const [stock, setStock] = useState([])
  const [completeStock, setCompleteStock] = useState([])
  const [savedCount, setSavedCount] = useState([])
  const [loading, setLoading] = useState(false)
  
  useEffect(() => {
    if (props.fetchData) {
      setSelectedShop([])
      setProductsList([])
    }
  }, [props.fetchData])

  const columns = [
    {
      title: 'Productos',
      dataIndex: 'product', // Field that is goint to be rendered
      render: text => <span style={{ textAlign: 'left' }}>{text}</span>,
    },
    {
      title: 'Contado',
      dataIndex: 'count', // Field that is goint to be rendered
      render: text => <span>{text}</span>,
    },
    {
      title: 'En inventario',
      dataIndex: 'inventory', // Field that is goint to be rendered
      render: text => <span>{text}</span>,
    },
    {
      title: 'Diferencia',
      dataIndex: 'diff', // Field that is goint to be rendered
      render: text => <span>{text}</span>,
    },
  ]

  const onCancel = () => {
    setSelectedShop([])
    setProductsList([])
    props.closable()
  }

  const fetchStock = storeId => {
    setLoading(true)

    inventorySrc
      .getInventory(storeId, false)
      .then(res => {
        if (res?.errors && Object.keys(res.errors)?.length > 0) {
          Utils.checkErrors(res)
          setLoading(false)
        } else {          
          const stock = res.data.map(s => ({ id: s.product_id, description: s.product_description }))
          setCompleteStock(res.data)
          setStock(stock)

          setLoading(false)
        }
      })
      .catch(err => {
        message.error('No se ha podido cargar la información, Intente nuevamente')
        setLoading(false)
      })
  }

  const validateProductsOnSave = () => {
    const productsCount = productsList.reduce((acc, v) => Object.assign(acc, { [v.product]: acc[v.product] ? acc[v.product] + 1 : 1 }), {})
    const duplicateProductsIds = Object.keys(productsCount).filter(k => productsCount[k] > 1)

    if (Utils.validateNull(selectedShop) || productsList.some(p => Utils.validateNull(p.product) || Utils.validateNull(p.quantity))) {
      return 'Todos los campos son obligatorios'
    }
    if (productsList.some(p => Utils.validateNumber(p.quantity))) {
      return 'El campo No. Unidades debe ser un número mayor o igual a cero'
    }
    if (duplicateProductsIds.length > 0) {
      const duplicateProducts = stock.flatMap(s => (duplicateProductsIds.some(id => Number(id) === Number(s.id)) ? s.description : []))
      return `No puede incluir mas de una vez el mismo producto (${[...new Set(duplicateProducts)].join(', ')})`
    }

    return false
  }

  const onSave = () => {
    const hasValidationMessage = validateProductsOnSave()
    if (hasValidationMessage) return message.warning(hasValidationMessage)

    const newInventory = getInventory(completeStock)
    const newDataSource = getDataCount(newInventory)
    const products = newDataSource.map(p => ({
      id: p.key,
      quantity: p.count,
      quantity_stock: completeStock.find(cs => Number(cs.product_id) === Number(p.key)).quantity,
    }))
    const data = {
      store_id: selectedShop,
      products,
    }

    setDataSource(newDataSource)
    setLoading(true)
    props.setFetchData(false)

    inventorySrc
      .saveCounts(data)
      .then(res => {
        if (res?.errors && Object.keys(res.errors)?.length > 0) {
          Utils.checkErrors(res)
          setLoading(false)
        } else {
          const savedCount = { store_id: res.store_id, stock_count_id: res.id }
          setSavedCount(savedCount)

          message.success('Información actualizada.')
          props.setFetchData(true)
          setIsSummary(true)
          setLoading(false)
        }
      })
      .catch(err => {
        message.error('Error al actualizar la información')
        setLoading(false)
      })
  }

  const onAdjust = isAdjust => {
    setLoading(true)

    if (isAdjust) {
      inventorySrc
        .adjustCounts(savedCount)
        .then(res => {
          if (res?.errors && Object.keys(res.errors)?.length > 0) {
            Utils.checkErrors(res)
            setLoading(false)
          } else {
            message.success('Ajuste por Conteo creado exitosamente.')
            setLoading(false)
            setIsSummary(false)
            setSelectedShop([])
            props.closable(true)
          }
        })
        .catch(err => {
          message.error('Error al crear ajuste por conteo')
          setLoading(false)
        })
    } else {
      inventorySrc
        .removeCount(savedCount.stock_count_id)
        .then(res => {
          if (res?.errors && Object.keys(res.errors)?.length > 0) {
            message.error(res.message)
            setLoading(false)
          } else {
            message.success('Ajuste por Conteo cancelado exitosamente.')
            setLoading(false)
            setIsSummary(false)
            setSelectedShop([])
            props.closable(true)
          }
        })
        .catch(err => {
          message.error('Error al eliminar ajuste por conteo')
          setLoading(false)
        })
    }
  }

  const getProducts = products => {
    let _products = products.map((p, i) => ({
      id: i + 1,
      product: p?.product,
      quantity: p?.units,
    }))
    setProductsList(_products)
  }

  const getProductsInfo = products => {
    let _products = products?.map(p => ({
      id: p?.product,
      name: p?.productName,
    }))
    setProductsInfo(_products)
  }

  const getInventory = inventory => {
    return inventory.map(d => ({
      product_id: d?.product_id,
      name: d?.product_description,
      quantity: d?.quantity,
    }))
  }

  const getDataCount = inventory => {
    return productsList.map(product => {
      let inventoryQuantity = inventory.find(p => Number(product.product) === Number(p.product_id)) || 0
      let productInfo = productsInfo.find(p => Number(product.product) === Number(p.id))
      let diff = product.quantity - (inventoryQuantity.quantity || 0)
      if (diff > 0) diff = `+${diff}`
      return {
        key: productInfo?.id,
        product: productInfo?.name,
        count: product?.quantity,
        inventory: inventoryQuantity?.quantity || 0,
        diff,
      }
    })
  }

  return (
    <Drawer placement="right" closable={false} onClose={onCancel} visible={props.visible} width={800}>
      {props.loading && (
        <div className="loading-spinner">
          <UISpinner />
        </div>
      )}
      {isSummary ? (
        <>
          <div>
            <Title> {'Resumen de conteo'} </Title>
            <Divider className={'divider-custom-margins-users'} />
            <Row gutter={16} className={'section-space-field'}>
              <Col xs={24} sm={24} md={24} lg={24}>
                <Table
                  className={'CustomTableClass'}
                  dataSource={dataSource}
                  columns={columns}
                  pagination={false}
                  loading={loading}
                  rowKey={(record, index) => index}
                />
              </Col>
            </Row>
          </div>
          <div>
            <Divider className={'divider-custom-margins-users'} />
            <Row gutter={16}>
              <Col xs={24} sm={24} md={24} lg={24}>
                <div className="text-right d-flex justify-end align-center">
                  <span className={'mr-3'}>¿Deseas ajustar tu inventario?</span>
                  <div>
                    <Button
                      type={'link'}
                      className="title-tendero new-button no-ajustar-btn product-margin-header-button"
                      onClick={() => onAdjust(false)}
                    >
                      No ajustar
                    </Button>
                    <Button htmlType="submit" className="title-tendero new-button" onClick={() => onAdjust(true)}>
                      Ajustar
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </>
      ) : (
        <>
          <div>
            <Title> {'Nuevo conteo'} </Title>
            <Divider className={'divider-custom-margins-users'} />
            <Row gutter={16} className={'section-space-field'}>
              <Col xs={12} sm={12} md={12} lg={12}>
                <GenericSelect
                  title={'¿Para qué tienda es?*'}
                  data={props.shopsList}
                  value={selectedShop}
                  handlerChange={option => {
                    setProductsList([])
                    setProductsInfo([])
                    setSelectedShop(option)
                    fetchStock(option)
                  }}
                  placeholder={'Elige una tienda'}
                />
              </Col>
            </Row>
            <Row gutter={16} className={'section-space-list'}>
              <Col xs={24} sm={24} md={24} lg={24}>
                <InventoryList
                  productList={stock}
                  listType={'COUNT'}
                  visible={props.visible}
                  products={getProducts}
                  productsInfo={getProductsInfo}
                  selectedShop={selectedShop}
                  loading={loading}
                />
              </Col>
            </Row>
          </div>
          <div>
            <Divider className={'divider-custom-margins-users'} />
            <Row gutter={16}>
              <Col xs={24} sm={24} md={24} lg={24}>
                <div className="text-right">
                  <div>
                    <Button loading={loading} type={'link'} className="cancel-button" onClick={() => onCancel()}>
                      Cancelar
                    </Button>
                    <Button loading={loading} htmlType="submit" className="title-tendero new-button" onClick={() => onSave()}>
                      Guardar y continuar
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </>
      )}
    </Drawer>
  )
}

export default CountsDrawer

CountsDrawer.defaultProps = {
  shopsList: [],
  loading: false,
}
